import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import Input from '../../components/Input';
import PageHeader from '../../components/PageHeader';
import PinCodeInput from '../../components/PinCodeInput';
import AppConfig from '../../config';
import { getDeviceInfo } from '../../hooks/useDeviceInfo';
import { t } from '../../lang';
import { pushSubscribe } from '../../serviceWorkerRegistration';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import TypesService from '../../services/TypesService';
import WebSocketService from '../../services/WebSocketService';
import { appSlice } from '../../store/slices';
import { authSlice } from '../../store/slices/auth';

import './SignIn.scss';

const pinLength = TypesService.PinCodeLength;

const isDevEnv = process.env.NODE_ENV === 'development';

const { routes } = AppConfig;

const SignIn = () => {
	const [loading, setLoading] = useState(false);

	const publicConfig = useSelector(store => store.app.publicConfig);
	const username = useSelector(state => state.auth.username);
	const pin = useSelector(state => state.auth.pin);
	const captchaConfirmRequestData = useSelector(state => state.app.captchaConfirmRequestData);
	const navigate = useNavigate();
	const isItTelegram = useSelector(state => state.app.isItTelegram);

	const intl = useIntl();
	const dispatch = useDispatch();

	const updateUsername = e => {
		dispatch(authSlice.actions.setUsername(e.target.value));
	};

	const onBlurUsername = e => {
		const value = e.target.value.trim();
		dispatch(authSlice.actions.setUsername(value));
	};

	const updatePin = pin => {
		dispatch(authSlice.actions.setPin(pin));
	};

	const onSignIn = async () => {
		const resultHandler = async response => {
			if (response?.Status !== 'Success') {
				if (response?.ErrorCode === 'AccountBlocked') {
					HelperService.showNotification({
						type: 'error',
						title: t('Your account has been blocked'),
					});
				} else if (response?.ErrorCode === 'LoginValidateLimitReachedError') {
					HelperService.showNotification({
						type: 'error',
						title: t('Exceeded the number of registration attempts! Login blocked for {min} min {sec} sec', {
							min: Math.floor(response?.Error.SecondsLeft / 60),
							sec: response?.Error.SecondsLeft % 60,
						}),
					});
				} else if (response?.ErrorCode === 'LoginValidateError') {
					HelperService.showNotification({
						type: 'error',
						title: t('Incorrect login'),
					});
				} else if (response?.ErrorCode === 'InvalidTelegramAccountTryLoginError') {
					HelperService.showNotification({
						type: 'error',
						title: t("Error! You're trying to login to Mobile account from wrong Telegram"),
					});
				} else if (response?.ErrorCode === 'DeviceNotFound') {
					const isItCaptchaRequest = !!captchaConfirmRequestData;
					navigate(routes.verifyDevice, { replace: isItCaptchaRequest });
					if (isItCaptchaRequest) dispatch(appSlice.actions.setCaptchaConfirmRequestData());
				} else if (response?.Error) {
					HelperService.showNotification({
						type: 'error',
						title: JSON.stringify(response),
					});
				} else if (response) {
					throw response;
				}
			} else {
				dispatch(appSlice.actions.setToken(response.Token));
				isDevEnv && sessionStorage.setItem('token', response.Token);
				navigate(routes.mainPage, { replace: !!captchaConfirmRequestData });
				dispatch(appSlice.actions.setCaptchaConfirmRequestData());
				HelperService.showNotification({
					type: 'success',
					title: t('Welcome back, {username}', { username }),
				});
				pushSubscribe();
				try {
					WebSocketService.DoConnection(publicConfig.apiUrl, response.Token);
				} catch (error) {
					console.log(error);
				}
			}
		};

		const failHandler = error => {
			RequestService.BaseErrorFunc(error);
		};

		try {
			setLoading(true);
			const deviceInfo = await getDeviceInfo();
			const { fingerprintId } = deviceInfo;
			const response = await RequestService.LoginByPinCode(username, pin, fingerprintId, resultHandler, failHandler);
			setLoading(false);
			resultHandler(response);
		} catch (error) {
			failHandler(error);
		}
	};

	const forgotPin = () => {
		navigate(routes.recoveryPin);
	};

	const formFilled = username.trim().length > 0 && !/\s/.test(username.trim()) && pin.length === pinLength;

	return (
		<div className='SignIn'>
			<PageHeader header={'Sign in'} subheader={'Enter your username and PIN-code'} />
			<div className='SignIn__body'>
				<div>
					<Input
						className='SignIn__username FormBlock'
						maxLength={30}
						onChange={updateUsername}
						onBlur={onBlurUsername}
						placeholder={intl.formatMessage({
							id: 'Enter username or invite name',
						})}
						value={username}
						autoFocus
					/>
					<PinCodeInput className='SignIn__pin' value={pin} onChange={updatePin} autoFocus={false} />
					{formFilled && (
						<Button className='SignIn__button' styleType='primary' onClick={onSignIn} loading={loading}>
							{t('Sign in')}
						</Button>
					)}
				</div>
				<Button className='SignIn__button' styleType='ghost' onClick={forgotPin}>
					{t('Forgot PIN-code?')}
				</Button>
			</div>
		</div>
	);
};

export default SignIn;
